// PC 단축키 라우팅(대표님 2026-10-24 "실제 웹앱은 PC로도 조종하니까 PC 최적화"). 순수 판정만 한다(DOM 없음): 눌린 키와 지금 화면 상태로 "무엇을 할지"를 돌려준다.
// 실제 실행은 ui/index.ts가 한다(열기 명령 전송, 가방 탭 지정, 도움말 켜기).
//
// 키 결정(충돌 조사: core/input.ts·skillbar.ts·rpg.ts·autoQuest.ts·fullscreen.ts·cameraGestures.ts·render/camera.ts):
//   전투 키 J 공격 · K/Shift 회피 · U I O L P 스킬 · Space 각성 · R T 물약 · E/Enter 상호작용 · WASD/방향키 이동 · 1~6 슬롯 · Tab 슬롯 쪽 — 이미 전부 쓰고 있다.
//   그 밖에 Q 자동 진행 · B 가방 · C 카메라 초기화 · F 전체 화면 · +/− 줌 · Esc 일시정지·닫기.
//   [2026-10-28 레인 C] 키 배치 프리셋(shared/keymap.ts): PC RPG식은 K = 스킬 창 · C = 스탯 창(회피는 Shift, 카메라 초기화는 Home), 액션식은 위 표 그대로(K 회피 · C 카메라).
//   창 키 판정은 windowKeyOf(code, preset) 하나가 정본이다. N(스킬)·G·M은 두 배치 모두 같다.
//   액션식에서 "J 퀘스트·I 가방·K 스킬"(로스트아크 관례)은 쓸 수 없다. 남은 글자 중 오른손 전투 손(J K L U I O P) 옆을 피해 왼손이 닿는 G를 퀘스트(Goal)로,
//   N을 스킬(WoW 특성 창 관례)로, M을 지도로 쓴다. 가방은 기존 B를 그대로 둔다. 도움말은 F1 또는 ?.
//   전부 마을에서만 창을 연다: 던전(전투 중)에서는 G·N·M이 아무 일도 하지 않는다(전투 입력을 가로채지 않는다).
import type { GameView } from '../shared/types';
import { Scene, combatScene } from '../shared/types';
import { windowKeyOf, type KeyPreset } from '../shared/keymap';

export type HotkeyAction = 'quest' | 'skills' | 'map' | 'stats' | 'help';

/** 단축키 안내(도움말 오버레이와 문서가 같은 표를 쓴다) */
export interface KeyHelpRow { readonly keys: readonly string[]; readonly label: string }
export interface KeyHelpGroup { readonly title: string; readonly rows: readonly KeyHelpRow[] }
/** 지금 키 배치의 안내 표(도움말 오버레이가 열 때마다 새로 만든다 — 프레임 루프 아님) */
export function keyHelpFor(preset: KeyPreset): readonly KeyHelpGroup[] {
  const rpg = preset === 'rpg';
  return [
    { title: '이동 · 전투', rows: [
      { keys: ['W', 'A', 'S', 'D'], label: '이동 (방향키도 가능)' },
      { keys: ['J'], label: '공격' },
      { keys: rpg ? ['Shift'] : ['K', 'Shift'], label: '회피' },
      { keys: ['U', 'I', 'O', 'L', 'P'], label: '스킬 (1~6 슬롯, Tab 쪽 전환)' },
      { keys: ['Space'], label: '각성' },
      { keys: ['R', 'T'], label: 'HP · MP 물약' },
      { keys: ['E', 'Enter'], label: '대화 · 상호작용' },
      { keys: ['Q'], label: '자동 진행 (마을 목표 · 던전 다음 방)' },
    ] },
    { title: '창', rows: [
      { keys: ['G'], label: '퀘스트 창 (마을)' },
      { keys: ['B'], label: '가방 · 장비 (마을)' },
      { keys: rpg ? ['K', 'N'] : ['N'], label: '스킬 트리 (마을)' },
      ...(rpg ? [{ keys: ['C'], label: '스탯 창 (마을)' }] : []),
      { keys: ['M'], label: '스테이지 지도 (마을)' },
      { keys: ['Esc'], label: '창 닫기 · 일시정지 · 설정' },
      { keys: ['F1', '?'], label: '이 단축키 안내' },
    ] },
    { title: '화면 · 카메라', rows: [
      { keys: ['마우스 휠', '+', '−'], label: '시야 거리 확대 · 축소' },
      { keys: ['휠 클릭', '우클릭 드래그'], label: '카메라 회전' },
      { keys: rpg ? ['Home'] : ['C', 'Home'], label: '카메라 초기화' },
      { keys: ['F'], label: '전체 화면' },
    ] },
  ];
}

/** 판정에 쓰는 지금 상태(GameView에서 뽑는다) */
export interface HotkeyState {
  readonly scene: 'title' | 'village' | 'dungeon' | 'other';
  readonly paused: boolean;
  /** NPC 창·가방 창·지도·퀘스트 창 중 하나라도 열려 있는가 */
  readonly anyWindow: boolean;
  readonly questOpen: boolean;
  readonly inventoryOpen: boolean;
  readonly stageMapOpen: boolean;
  /** 이동 연출 중 */
  readonly traveling: boolean;
  readonly helpOpen: boolean;
}

export function hotkeyStateOf(v: GameView, helpOpen: boolean): HotkeyState {
  const sc = v.flow.scene;
  const vv = v.village;
  return {
    scene: sc === Scene.Title ? 'title' : sc === Scene.Village ? 'village' : combatScene(sc) ? 'dungeon' : 'other',
    paused: v.flow.paused,
    anyWindow: vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen,
    questOpen: vv.questOpen,
    inventoryOpen: vv.inventoryOpen,
    stageMapOpen: vv.stageMapOpen,
    traveling: v.flow.travelTo > 0,
    helpOpen,
  };
}

/** 키 이벤트에서 필요한 필드만 */
export interface KeyLike { readonly code: string; readonly key?: string; readonly repeat?: boolean; readonly ctrlKey?: boolean; readonly metaKey?: boolean; readonly altKey?: boolean; readonly shiftKey?: boolean }

/** 판정 결과: open = 그 창을 연다, close = 열린 창을 닫는다(같은 키로 다시 누르기), help = 도움말 켜기/끄기, null = 아무 일 없음 */
export type HotkeyResult = { readonly type: 'open'; readonly action: 'quest' | 'skills' | 'map' | 'stats' } | { readonly type: 'close' } | { readonly type: 'help' } | null;

/** F1 또는 ?(Shift+/)인가 */
export function isHelpKey(e: KeyLike): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  return e.code === 'F1' || (e.code === 'Slash' && e.shiftKey === true) || e.key === '?';
}

/**
 * 키 하나를 어떻게 처리할지. 입력란에 타이핑 중이면(typing) 모두 무시한다.
 * G·N·M(+ rpg 배치의 K 스킬·C 스탯): 마을 장면에서 일시정지·다른 창·이동 연출이 없을 때 그 창을 연다. 같은 창이 이미 열려 있으면 닫는다(토글). 던전·타이틀에서는 null.
 * F1·?: 어디서든 도움말 토글(도움말이 열려 있으면 닫기).
 */
export function routeHotkey(e: KeyLike, s: HotkeyState, typing: boolean, preset: KeyPreset = 'action'): HotkeyResult {
  if (typing || e.repeat) return null;
  if (isHelpKey(e)) return { type: 'help' };
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return null;
  const action = windowKeyOf(e.code, preset);
  if (!action || s.helpOpen) return null;
  if (s.scene !== 'village' || s.paused || s.traveling) return null;
  // 같은 키로 닫기: 퀘스트=G, 스킬=N(가방 창이 열려 있을 때), 지도=M
  if (action === 'quest' && s.questOpen) return { type: 'close' };
  if ((action === 'skills' || action === 'stats') && s.inventoryOpen) return { type: 'close' };
  if (action === 'map' && s.stageMapOpen) return { type: 'close' };
  if (s.anyWindow) return null;
  return { type: 'open', action };
}

/** 키 이벤트 대상이 글자 입력란인가 */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as (HTMLElement & { isContentEditable?: boolean }) | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
}

/**
 * 오른쪽 위 아이콘 메뉴 줄(menuBar.ts) 단추 판정(순수). 단축키와 같은 규칙: 창은 마을에서만, 일시정지·이동 연출·도움말 중에는 열지 않는다.
 * 설정(톱니)은 마을·던전 어디서나 일시정지 창(설정·전체 화면·카메라·귀환서·마을로)을 연다. 다른 창이 열려 있으면 닫고 연다(close).
 * 같은 창 단추를 다시 누르면 닫는다(가방·퀘스트·지도; 스킬·스탯은 가방 창이 열려 있으면 단추로 닫기). 영웅은 메뉴 줄이 직접 고르기 창을 연다.
 */
export type MenuOpen = 'pause' | 'bag' | 'skill' | 'stat' | 'set' | 'quest' | 'map' | 'daily' | 'shop';
export function routeMenu(id: string, s: HotkeyState): { readonly open: MenuOpen | null; readonly close: boolean } | null {
  if (s.helpOpen || s.paused) return null;
  if (id === 'settings') return s.scene === 'village' || s.scene === 'dungeon' ? { open: 'pause', close: false } : null;
  if (s.scene !== 'village' || s.traveling) return null;
  if (id === 'quest' && s.questOpen) return { open: null, close: true };
  if (id === 'map' && s.stageMapOpen) return { open: null, close: true };
  if (id === 'bag' && s.inventoryOpen) return { open: null, close: true };
  if (id === 'bag' || id === 'skill' || id === 'stat' || id === 'set' || id === 'quest' || id === 'map' || id === 'daily' || id === 'shop') return { open: id, close: s.anyWindow };
  return null;
}
